import className from 'classnames/bind';
import { motion, useViewportScroll, useTransform, transform } from 'framer-motion';
import React from 'react';

import Heading from 'components/shared/heading';
import { BREAKPOINT_LG, BREAKPOINT_MD, BREAKPOINT_SM } from 'constants/breakpoints';
import useDeviceType, { DEVICE_TYPE } from 'hooks/use-device-type';
import useSectionOffset from 'hooks/use-section-offset';
import useWindowWidth from 'hooks/use-window-width';
import StarOutlined from 'images/pages/home/picture-this/star-outlined.inline.svg';
import Star from 'images/pages/home/picture-this/star.inline.svg';

import styles from './picture-this.module.scss';
import LoopVideo from './videos/line_loop.mp4';
import StartVideo from './videos/line_start.mp4';

const cx = className.bind(styles);

// const VARIANTS_INTERVALS = [0, 0.166, 0.333, 0.499, 0.666, 1];
const VARIANTS_INTERVALS = [0, 0.15, 0.3, 0.45, 0.6, 0.75];
const VARIANTS_TEXT_Y = [
  ['100vh', '50vh', '30vh', '0vh', '0vh', '0vh'],
  ['100vh', '100vh', '50vh', '30vh', '0vh', '0vh'],
  ['100vh', '100vh', '100vh', '50vh', '30vh', '0vh'],
];

const KICKER_HEIGHT_REM = {
  xl: 2.5,
  lg: 2.5,
  md: 3.64,
  sm: 6.76,
};
const TITLE_HEIGHT_REM = {
  xl: 27,
  lg: 31.64,
  md: 39.84375,
  sm: 94.2,
};
const TITLE_MARGIN_TOP_REM = {
  xl: 3.75,
  lg: 3.75,
  md: 5.2,
  sm: 9.66,
};
const DESCRIPTION_HEIGHT_REM = {
  xl: 2.75,
  lg: 2.75,
  md: 4.427,
  sm: 8.2,
};
const DESCRIPTION_MARGIN_TOP_REM = {
  xl: 1.875,
  lg: 1.875,
  md: 2.6,
  sm: 4.8,
};
const CONTENT_HEIGHT_REM = {
  xl: 38.125,
  lg: 42.965,
  md: 53.10375,
  sm: 123.82,
};

const getMargin = (element, breakpoint) => {
  switch (element) {
    case 'kicker': {
      return `calc(100vh - (100vh - ${CONTENT_HEIGHT_REM[breakpoint]}rem) / 2 - ${KICKER_HEIGHT_REM[breakpoint]}rem)`;
    }
    case 'title': {
      return `calc(100vh - (100vh - ${CONTENT_HEIGHT_REM[breakpoint]}rem) / 2 - ${KICKER_HEIGHT_REM[breakpoint]}rem  - ${TITLE_MARGIN_TOP_REM[breakpoint]}rem - ${TITLE_HEIGHT_REM[breakpoint]}rem)`;
    }
    case 'description': {
      return `calc(100vh - (100vh - ${CONTENT_HEIGHT_REM[breakpoint]}rem) / 2 - ${KICKER_HEIGHT_REM[breakpoint]}rem  - ${TITLE_MARGIN_TOP_REM[breakpoint]}rem - ${TITLE_HEIGHT_REM[breakpoint]}rem - ${DESCRIPTION_MARGIN_TOP_REM[breakpoint]}rem - ${DESCRIPTION_HEIGHT_REM[breakpoint]}rem)`;
    }
    default: {
      return 0;
    }
  }
};

const PictureThis = () => {
  const sectionRef = React.useRef();
  const { scrollY } = useViewportScroll();

  const [sectionStart, sectionEnd, sectionHidden] = useSectionOffset(sectionRef, true);

  const deviceType = useDeviceType();
  const windowWidth = useWindowWidth();

  const inputRange = VARIANTS_INTERVALS.map(
    (input) => sectionStart + (sectionEnd - sectionStart) * input
  );

  const getLineStyles = (lineNumber) => {
    /* eslint-disable react-hooks/rules-of-hooks */
    const linePositionY = useTransform(scrollY, inputRange, VARIANTS_TEXT_Y[lineNumber]);
    return { y: linePositionY };
  };

  const [hasChangedVideoSource, setHasChangedVideoSource] = React.useState(false);

  React.useEffect(() => {
    const startVideoElement = document.getElementById('start-video');
    const loopVideoElement = document.getElementById('loop-video');

    const START_VIDEO_DURATION = 0.64;

    const kickerElement = document.getElementById('picture-this-kicker');
    const titleElement = document.getElementById('picture-this-title');
    const descriptionElement = document.getElementById('picture-this-description');

    const scrollHandler = (evt) => {
      if (
        deviceType === DEVICE_TYPE.mobile.portrait ||
        deviceType === DEVICE_TYPE.mobile.landscape
      ) {
        return;
      }

      const videoStartOffset = sectionStart + (sectionEnd - sectionStart) * 0.2;
      const fixedPositionOffset = sectionStart + (sectionEnd - sectionStart) * 0.875;
      const videoEndOffset = sectionStart + (sectionEnd - sectionStart) * 0.675;

      // change time of start video on scroll
      if (
        startVideoElement &&
        window.scrollY >= videoStartOffset &&
        window.scrollY <= videoEndOffset
      ) {
        const newTime = transform(
          window.scrollY,
          [videoStartOffset, videoEndOffset],
          [0, START_VIDEO_DURATION]
        );
        if (isFinite(newTime)) {
          startVideoElement.currentTime = newTime;
        }
      }

      // change start video to loop video
      if (
        window.scrollY > videoEndOffset &&
        window.scrollY <= sectionEnd &&
        !hasChangedVideoSource
      ) {
        if (startVideoElement) {
          loopVideoElement.style.opacity = 1;
          startVideoElement.style.opacity = 0;
          loopVideoElement.setAttribute('loop', true);
          loopVideoElement.setAttribute('autoplay', true);
          loopVideoElement.play();
        }
        setHasChangedVideoSource(true);
        descriptionElement.style.opacity = 1;
      }

      // play loop video only if the section is visible
      if (
        window.scrollY >= sectionStart &&
        window.scrollY <= sectionHidden &&
        hasChangedVideoSource
      ) {
        if (startVideoElement) {
          loopVideoElement.play();
        }
        descriptionElement.style.opacity = 1;
      }

      if (window.scrollY < sectionStart && hasChangedVideoSource) {
        loopVideoElement.pause();
        descriptionElement.style.opacity = 0;
      }

      if (window.scrollY > sectionHidden && hasChangedVideoSource) {
        loopVideoElement.pause();
        descriptionElement.style.opacity = 0;
      }

      // add margins to prevent the elements sticking to the section bottom
      const addBottomMargins = () => {
        if (
          deviceType === DEVICE_TYPE.mobile.portrait ||
          deviceType === DEVICE_TYPE.mobile.landscape
        ) {
          return;
        }

        let currentBreakpoint = 'xl';
        if (windowWidth > BREAKPOINT_LG) {
          currentBreakpoint = 'xl';
        } else if (windowWidth > BREAKPOINT_MD) {
          currentBreakpoint = 'lg';
        } else if (windowWidth > BREAKPOINT_SM) {
          currentBreakpoint = 'md';
        } else {
          currentBreakpoint = 'sm';
        }
        // (screen height - margin top - element height)
        kickerElement.style.marginBottom = getMargin('kicker', currentBreakpoint);
        titleElement.style.marginBottom = getMargin('title', currentBreakpoint);
        descriptionElement.style.marginBottom = getMargin('description', currentBreakpoint);
      };

      const removeBottomMargins = () => {
        if (
          deviceType === DEVICE_TYPE.mobile.portrait ||
          deviceType === DEVICE_TYPE.mobile.landscape
        ) {
          return;
        }

        kickerElement.style.marginBottom = 0;
        titleElement.style.marginBottom = 0;
        descriptionElement.style.marginBottom = 0;
      };

      if (window.scrollY > fixedPositionOffset && window.scrollY <= sectionHidden) {
        addBottomMargins();
      }

      if (window.scrollY <= fixedPositionOffset || window.scrollY > sectionHidden) {
        removeBottomMargins();
      }
    };

    if (sectionEnd !== null && sectionStart !== null) {
      document.addEventListener('scroll', scrollHandler);
    }

    return () => document.removeEventListener('scroll', scrollHandler);
  }, [deviceType, windowWidth, sectionStart, sectionEnd, sectionHidden, hasChangedVideoSource]);

  const TitleLarge = () => {
    return (
      <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
        <motion.span style={getLineStyles(0)}>Buying</motion.span>
        <motion.span style={getLineStyles(1)}>Shoes</motion.span>
        <motion.span style={getLineStyles(2)}>
          Is&nbsp;
          <span className={cx('title-hard', hasChangedVideoSource && 'title-hard-accented')}>
            Hard
            <div className={cx('title-hard-star')}>
              <StarOutlined />
            </div>
          </span>
        </motion.span>
      </Heading>
    );
  };

  const TitleLargeStatic = () => {
    return (
      <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
        <span>Buying</span>
        <span>Shoes</span>
        <span>
          Is&nbsp;
          <span className={cx('title-hard', 'title-hard-accented')}>
            Hard
            <div className={cx('title-hard-star')}>
              <StarOutlined />
            </div>
          </span>
        </span>
      </Heading>
    );
  };

  const TitleSmall = () => {
    return (
      <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
        <span>
          <span>Buy&#8209;</span>
          <span>ing</span>
        </span>
        <span>
          <span>Sho&#8209;</span>
          <span>es&nbsp;is</span>
        </span>
        <span>
          <span className={cx('title-hard', 'title-hard-accented')}>
            Hard
            <div className={cx('title-hard-star')}>
              <StarOutlined />
            </div>
          </span>
        </span>
      </Heading>
    );
  };

  return (
    <section className={cx('wrapper')} ref={sectionRef}>
      <Heading
        className={cx('kicker')}
        tag="p"
        size="md"
        theme="secondary"
        id="picture-this-kicker"
      >
        PICTURE THIS
      </Heading>
      <div className={cx('title-wrapper')} id="picture-this-title">
        {!(
          deviceType === DEVICE_TYPE.mobile.portrait || deviceType === DEVICE_TYPE.mobile.landscape
        ) && (
          <>
            <TitleLarge />
            {deviceType === DEVICE_TYPE.desktop && (
              <>
                <div className={cx('video', 'video-start')}>
                  <video id="start-video" src={StartVideo} type="video/mp4" muted />
                </div>
                <div className={cx('video', 'video-loop')}>
                  <video id="loop-video" src={LoopVideo} type="video/mp4" muted />
                </div>
              </>
            )}

            {deviceType !== DEVICE_TYPE.desktop && (
              <div className={cx('video', 'video-loop')}>
                <video id="loop-video" src={LoopVideo} type="video/mp4" muted loop autoPlay />
              </div>
            )}
          </>
        )}

        {deviceType === DEVICE_TYPE.mobile.landscape && <TitleLargeStatic />}
        {deviceType === DEVICE_TYPE.mobile.portrait && <TitleSmall />}

        {(deviceType === DEVICE_TYPE.mobile.portrait ||
          deviceType === DEVICE_TYPE.mobile.landscape) && (
          <div className={cx('video', 'video-loop')}>
            <video id="loop-video" src={LoopVideo} type="video/mp4" muted loop autoPlay />
          </div>
        )}
      </div>

      <span className={cx('description')} id="picture-this-description">
        SNKRS every saturday, getting through splash just to not be able to pay
        <div className={cx('description-star')}>
          <Star />
        </div>
      </span>
    </section>
  );
};

export default PictureThis;
